import React, { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Upload, FileSpreadsheet, Users, Globe, AlertCircle } from 'lucide-react';
import Select, { StylesConfig } from 'react-select';
import api from '../services/api';

interface CreateWorkflowModalProps {
  onClose: () => void;
}

interface AdminOption {
  value: string;
  label: string;
}

// Custom styles for react-select to match dark theme
const selectStyles: StylesConfig<AdminOption> = {
  control: (base) => ({
    ...base,
    backgroundColor: 'rgba(31, 41, 55, 0.5)', // bg-gray-800/50
    borderColor: 'rgb(55, 65, 81)', // border-gray-700
    borderRadius: '0.5rem',
    minHeight: '48px',
    boxShadow: 'none',
    '&:hover': {
      borderColor: 'rgb(75, 85, 99)', // hover:border-gray-600
    },
    '&:focus-within': {
      borderColor: 'rgb(249, 115, 22)', // focus:border-orange-500
      outline: 'none',
    },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: 'rgb(17, 24, 39)', // bg-gray-900
    border: '1px solid rgb(55, 65, 81)',
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
    zIndex: 9999,
  }),
  menuList: (base) => ({
    ...base,
    padding: '0.25rem',
    maxHeight: '300px',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? 'rgba(249, 115, 22, 0.2)' // orange focus
      : state.isSelected
      ? 'rgba(249, 115, 22, 0.3)' // orange selected
      : 'transparent',
    color: 'white',
    cursor: 'pointer',
    padding: '0.5rem 0.75rem',
    borderRadius: '0.25rem',
    '&:active': {
      backgroundColor: 'rgba(249, 115, 22, 0.4)',
    },
  }),
  input: (base) => ({
    ...base,
    color: 'white',
  }),
  placeholder: (base) => ({
    ...base,
    color: 'rgb(107, 114, 128)', // text-gray-500
  }),
  singleValue: (base) => ({
    ...base,
    color: 'white',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: 'rgb(107, 114, 128)',
    '&:hover': {
      color: 'rgb(156, 163, 175)',
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: 'rgb(107, 114, 128)',
    '&:hover': {
      color: 'rgb(239, 68, 68)', // red on hover
    },
  }),
};

const CreateWorkflowModal: React.FC<CreateWorkflowModalProps> = ({ onClose }) => {
  const [mode, setMode] = useState<'manual' | 'sheet'>('sheet');
  const [assignmentMode, setAssignmentMode] = useState<'bulk' | 'individual'>('bulk');
  const [domainAdminMapping, setDomainAdminMapping] = useState<Record<string, string>>({});
  const [loadedDomains, setLoadedDomains] = useState<string[]>([]);
  const [isLoadingDomains, setIsLoadingDomains] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    customer_email: '',
    spreadsheet_url: '',
    admin_account: '',
    domains: [''],
    nameservers: ['ns1.example.com', 'ns2.example.com'],
    users: [{ email: '', first_name: '', last_name: '', password: '' }],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const queryClient = useQueryClient();

  const { data: adminAccounts } = useQuery({
    queryKey: ['admin-accounts'],
    queryFn: api.getAdminAccounts,
  });

  // Convert admin accounts to react-select options format
  const adminOptions = useMemo<AdminOption[]>(() => {
    return (adminAccounts || []).map((account: any) => ({
      value: account.email,
      label: account.email,
    }));
  }, [adminAccounts]);

  const createWorkflowMutation = useMutation({
    mutationFn: (data: any) => 
      mode === 'sheet' 
        ? api.createWorkflowFromSheet(data)
        : api.createWorkflow(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
      onClose();
    },
    onError: (error: any) => {
      setErrors({ submit: error.message || 'Failed to create workflow' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (mode === 'sheet') {
      if (!formData.spreadsheet_url) {
        setErrors({ spreadsheet_url: 'Spreadsheet URL is required' });
        return;
      }
      if (assignmentMode === 'bulk' && !formData.admin_account) {
        setErrors({ admin_account: 'Admin account is required for bulk mode' });
        return;
      }
      if (assignmentMode === 'individual' && Object.keys(domainAdminMapping).length === 0) {
        setErrors({ domain_mapping: 'Please assign admin accounts to domains' });
        return;
      }
      createWorkflowMutation.mutate({
        spreadsheet_url: formData.spreadsheet_url,
        admin_account: assignmentMode === 'bulk' ? formData.admin_account : '',
        assignment_mode: assignmentMode,
        domain_admin_mapping: assignmentMode === 'individual' ? domainAdminMapping : {},
      });
    } else {
      // Manual mode validation
      if (!formData.customer_id) {
        setErrors({ customer_id: 'Customer ID is required' });
        return;
      }
      createWorkflowMutation.mutate({
        customer_id: formData.customer_id,
        data: {
          name: formData.customer_name,
          email: formData.customer_email,
          domains: formData.domains.filter(d => d),
          nameservers: formData.nameservers.filter(n => n),
          users: formData.users.filter(u => u.email),
        },
      });
    }
  };

  const addDomain = () => {
    setFormData({ ...formData, domains: [...formData.domains, ''] });
  };

  const updateDomain = (index: number, value: string) => {
    const newDomains = [...formData.domains];
    newDomains[index] = value;
    setFormData({ ...formData, domains: newDomains });
  };

  const removeDomain = (index: number) => {
    setFormData({ 
      ...formData, 
      domains: formData.domains.filter((_, i) => i !== index) 
    });
  };

  const addUser = () => {
    setFormData({ 
      ...formData, 
      users: [...formData.users, { email: '', first_name: '', last_name: '', password: '' }] 
    });
  };

  const updateUser = (index: number, field: string, value: string) => {
    const newUsers = [...formData.users];
    newUsers[index] = { ...newUsers[index], [field]: value };
    setFormData({ ...formData, users: newUsers });
  };

  const removeUser = (index: number) => {
    setFormData({
      ...formData,
      users: formData.users.filter((_, i) => i !== index)
    });
  };

  const loadDomainsFromSheet = async () => {
    if (!formData.spreadsheet_url) {
      setErrors({ spreadsheet_url: 'Please enter a spreadsheet URL first' });
      return;
    }

    setIsLoadingDomains(true);
    setErrors({});

    try {
      const result = await api.parseSpreadsheet(formData.spreadsheet_url);
      setLoadedDomains(result.domains || []);

      // Initialize domain-admin mapping with empty values
      const initialMapping: Record<string, string> = {};
      (result.domains || []).forEach((domain: string) => {
        initialMapping[domain] = '';
      });
      setDomainAdminMapping(initialMapping);

      setErrors({});
    } catch (error: any) {
      setErrors({ spreadsheet_url: error.response?.data?.error || 'Failed to load domains from spreadsheet' });
      setLoadedDomains([]);
    } finally {
      setIsLoadingDomains(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <h2 className="text-2xl font-bold premium-gradient">Create New Workflow</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Mode Selector */}
        <div className="p-6 border-b border-gray-800">
          <div className="flex gap-4">
            <button
              onClick={() => setMode('sheet')}
              className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                mode === 'sheet' 
                  ? 'border-orange-500 bg-orange-500/10' 
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              <FileSpreadsheet className="w-8 h-8 text-orange-400 mx-auto mb-2" />
              <div className="font-semibold text-white">Google Sheets Import</div>
              <div className="text-sm text-gray-400 mt-1">Import from spreadsheet</div>
            </button>
            <button
              onClick={() => setMode('manual')}
              className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                mode === 'manual' 
                  ? 'border-orange-500 bg-orange-500/10' 
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              <Users className="w-8 h-8 text-orange-400 mx-auto mb-2" />
              <div className="font-semibold text-white">Manual Entry</div>
              <div className="text-sm text-gray-400 mt-1">Enter details manually</div>
            </button>
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-280px)]">
          {mode === 'sheet' ? (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Google Sheets URL
                </label>
                <div className="relative">
                  <FileSpreadsheet className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                  <input
                    type="url"
                    value={formData.spreadsheet_url}
                    onChange={(e) => setFormData({ ...formData, spreadsheet_url: e.target.value })}
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
                  />
                </div>
                {errors.spreadsheet_url && (
                  <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.spreadsheet_url}
                  </p>
                )}
                <p className="text-gray-500 text-sm mt-2">
                  Spreadsheet must contain columns: Domain, Users, First Name, Last Name, Password
                </p>
                <button
                  type="button"
                  onClick={loadDomainsFromSheet}
                  disabled={isLoadingDomains || !formData.spreadsheet_url}
                  className="mt-3 px-4 py-2 bg-gradient-to-r from-orange-500 to-yellow-500 text-black font-medium rounded-lg hover:from-orange-600 hover:to-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                >
                  {isLoadingDomains ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Loading Domains...
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="w-4 h-4" />
                      {loadedDomains.length > 0 ? `${loadedDomains.length} Domains Loaded` : 'Load Domains'}
                    </>
                  )}
                </button>
              </div>

              {loadedDomains.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-3">
                    Assignment Mode
                  </label>
                  <div className="flex gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => setAssignmentMode('bulk')}
                      className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                        assignmentMode === 'bulk'
                          ? 'border-orange-500 bg-orange-500/10 text-white'
                          : 'border-gray-700 text-gray-400 hover:border-gray-600'
                      }`}
                    >
                      <div className="font-medium">Bulk</div>
                      <div className="text-xs mt-1 opacity-80">One admin for all domains</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setAssignmentMode('individual')}
                      className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                        assignmentMode === 'individual'
                          ? 'border-orange-500 bg-orange-500/10 text-white'
                          : 'border-gray-700 text-gray-400 hover:border-gray-600'
                      }`}
                    >
                      <div className="font-medium">Individual</div>
                      <div className="text-xs mt-1 opacity-80">Admin per domain</div>
                    </button>
                  </div>

                  {assignmentMode === 'bulk' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Admin Account
                  </label>
                  <Select<AdminOption>
                    options={adminOptions}
                    value={adminOptions.find(opt => opt.value === formData.admin_account) || null}
                    onChange={(selected) => setFormData({ ...formData, admin_account: selected?.value || '' })}
                    styles={selectStyles}
                    placeholder="Search and select admin account..."
                    isClearable
                    isSearchable
                  />
                  {errors.admin_account && (
                    <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.admin_account}
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Domain-to-Admin Mapping
                  </label>
                  <div className="p-4 bg-gray-800/30 border border-gray-700 rounded-lg">
                    {loadedDomains.length === 0 ? (
                      <p className="text-gray-500 text-sm">
                        Please click "Load Domains" button first to load domains from spreadsheet
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {loadedDomains.map(domain => (
                          <div key={domain} className="flex items-center gap-2 p-2 bg-gray-900/50 rounded">
                            <span className="text-sm text-gray-300 w-48 truncate" title={domain}>
                              {domain}
                            </span>
                            <div className="flex-1">
                              <Select<AdminOption>
                                options={adminOptions}
                                value={adminOptions.find(opt => opt.value === domainAdminMapping[domain]) || null}
                                onChange={(selected) => setDomainAdminMapping({
                                  ...domainAdminMapping,
                                  [domain]: selected?.value || ''
                                })}
                                styles={{
                                  ...selectStyles,
                                  control: (base) => ({
                                    ...selectStyles.control!(base, {} as any),
                                    minHeight: '36px',
                                    fontSize: '0.875rem',
                                  }),
                                }}
                                placeholder="Search admin..."
                                isClearable
                                isSearchable
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {errors.domain_mapping && (
                    <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.domain_mapping}
                    </p>
                  )}
                </div>
              )}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Customer Information */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-orange-400" />
                  Customer Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Customer ID *
                    </label>
                    <input
                      type="text"
                      value={formData.customer_id}
                      onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Customer Name
                    </label>
                    <input
                      type="text"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Customer Email
                    </label>
                    <input
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500"
                    />
                  </div>
                </div>
              </div>

              {/* Domains */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-orange-400" />
                  Domains
                </h3>
                {formData.domains.map((domain, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={domain}
                      onChange={(e) => updateDomain(index, e.target.value)}
                      placeholder="example.com"
                      className="flex-1 px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeDomain(index)}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addDomain}
                  className="btn-secondary text-sm"
                >
                  Add Domain
                </button>
              </div>

              {/* Users */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-orange-400" />
                  User Accounts
                </h3>
                {formData.users.map((user, index) => (
                  <div key={index} className="p-4 bg-gray-800/30 rounded-lg mb-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <input
                        type="email"
                        value={user.email}
                        onChange={(e) => updateUser(index, 'email', e.target.value)}
                        placeholder="Email"
                        className="px-3 py-2 bg-gray-900/50 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-orange-500"
                      />
                      <input
                        type="text"
                        value={user.first_name}
                        onChange={(e) => updateUser(index, 'first_name', e.target.value)}
                        placeholder="First Name"
                        className="px-3 py-2 bg-gray-900/50 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-orange-500"
                      />
                      <input
                        type="text"
                        value={user.last_name}
                        onChange={(e) => updateUser(index, 'last_name', e.target.value)}
                        placeholder="Last Name"
                        className="px-3 py-2 bg-gray-900/50 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-orange-500"
                      />
                      <div className="flex gap-2">
                        <input
                          type="password"
                          value={user.password}
                          onChange={(e) => updateUser(index, 'password', e.target.value)}
                          placeholder="Password"
                          className="flex-1 px-3 py-2 bg-gray-900/50 border border-gray-700 rounded text-white text-sm focus:outline-none focus:border-orange-500"
                        />
                        <button
                          type="button"
                          onClick={() => removeUser(index)}
                          className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addUser}
                  className="btn-secondary text-sm"
                >
                  Add User
                </button>
              </div>
            </div>
          )}

          {errors.submit && (
            <div className="mt-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {errors.submit}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createWorkflowMutation.isPending}
              className="btn-primary flex items-center gap-2"
            >
              {createWorkflowMutation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Create Workflow
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateWorkflowModal;